import { FeatherIcon } from '../../components/FeatherIcon'
import { useStatsT } from './statsContext'
import { customDays, periodOptions, periodSubtitle, timezoneLabel, utcDay, type Period, type Range } from './statisticsData'

/** OnlyFans' period dropdown: the chosen period, its dates, and the list of periods. */
export function PeriodBar({
  period,
  range,
  withTz,
  open,
  onToggle,
  onPick,
}: {
  period: Period
  range: Range | null
  withTz: boolean
  open: boolean
  onToggle: () => void
  onPick: (key: string) => void
}) {
  const { t, intl } = useStatsT()
  const options = periodOptions(new Date(), intl)
  const labelOf = (o: { label?: string; text?: string }) => (o.label ? t(`period.${o.label}`) : (o.text ?? ''))
  const current = period.key === 'custom' ? t('period.custom') : labelOf(options.find((o) => o.key === period.key) ?? { label: '30d' })
  const tz = withTz ? ` ${t('localTime', { tz: timezoneLabel() })}` : ''
  return (
    <div className="ofx-period" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="ofx-period-btn" aria-haspopup="menu" aria-expanded={open} onClick={onToggle}>
        <span>
          <strong>{current}</strong>
          <small>
            {periodSubtitle(period.key, range, intl)}
            {tz}
          </small>
        </span>
        <FeatherIcon name="chevron-down" size={18} />
      </button>
      {open && (
        <div className="ofx-period-menu" role="menu" aria-label={t('period.label')}>
          {options.map((o) => (
            <div key={o.key} role="none">
              {o.divider && <div className="ofx-period-divider" />}
              <button type="button" role="menuitem" className={o.key === period.key ? 'is-on' : ''} onClick={() => onPick(o.key)}>
                {labelOf(o)}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** "N DAYS SELECTED": two UTC days, applied together. */
export function CustomRange({
  value,
  onChange,
  onCancel,
  onApply,
}: {
  value: { from: string; to: string }
  onChange: (which: 'from' | 'to', day: string) => void
  onCancel: () => void
  onApply: () => void
}) {
  const { t } = useStatsT()
  const today = utcDay(new Date())
  const days = customDays(value.from, value.to)
  const set = (which: 'from' | 'to', day: string) => /^\d{4}-\d{2}-\d{2}$/.test(day) && onChange(which, day)
  return (
    <div className="ofp-modal-back" onClick={onCancel}>
      <div className="ofp-modal ofx-custom" role="dialog" aria-modal="true" aria-label={t('period.custom')} onClick={(e) => e.stopPropagation()}>
        <div className="ofx-custom-title">{t('daysSelected', { n: days })}</div>
        <div className="ofx-custom-inputs">
          <input type="date" className="ofp-input" value={value.from} max={value.to || today} aria-label={t('from')} onChange={(e) => set('from', e.target.value)} />
          <span aria-hidden="true">—</span>
          <input type="date" className="ofp-input" value={value.to} min={value.from} max={today} aria-label={t('to')} onChange={(e) => set('to', e.target.value)} />
        </div>
        <div className="ofp-modal-actions">
          <button type="button" className="lib-btn lib-btn--ghost" onClick={onCancel}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!days} onClick={onApply}>
            {t('apply')}
          </button>
        </div>
      </div>
    </div>
  )
}
